import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import HeaderNav from "@/components/HeaderNav";
import ErpLayout from "@/components/ErpLayout";
import Login from "@/pages/Login";
import PosTerminal from "@/pages/PosTerminal";
import CatalogoPack from "@/pages/CatalogoPack";
import Etiquetas from "@/pages/Etiquetas";
import Mermas from "@/pages/Mermas";
import Traspasos from "@/pages/Traspasos";
import Trazabilidad from "@/pages/Trazabilidad";
import Admin from "@/pages/Admin";
import PedidosPagos from "@/pages/PedidosPagos";
import TraspasosVentas from "@/pages/TraspasosVentas";
import Promociones from "@/pages/Promociones";
import Cortes from "@/pages/Cortes";
import Reportes from "@/pages/Reportes";
import Clientes from "@/pages/Clientes";
import Comisiones from "@/pages/Comisiones";
import Gastos from "@/pages/Gastos";
import ErpDashboard from "@/pages/ErpDashboard";
import Usuarios from "@/pages/Usuarios";
import Inventario from "@/pages/Inventario";
import Stock from "@/pages/Stock";
import MisVentas from "@/pages/MisVentas";
import Conciliacion from "@/pages/Conciliacion";
import Tesoreria from "@/pages/Tesoreria";
import Bancos from "@/pages/Bancos";
import Ecommerce from "@/pages/Ecommerce";
import CobroSistema from "@/pages/CobroSistema";
import ReportesIA from "@/pages/ReportesIA";
import Resurtido from "@/pages/Resurtido";
import Compras from "@/pages/Compras";
import Metas from "@/pages/Metas";
import SaldosFavor from "@/pages/SaldosFavor";
import Alertas from "@/pages/Alertas";
import Migracion from "@/pages/Migracion";
import Ayuda from "@/pages/Ayuda";
import Activar from "@/pages/Activar";
import InversionistaLayout from "@/components/InversionistaLayout";
import InversionistaResumen from "@/pages/InversionistaResumen";
import InversionistaUtilidades from "@/pages/InversionistaUtilidades";

const ERP_ROLES = ["gran_admin", "coordinador", "contabilidad"];
const INVERSIONISTA = "inversionista";

function Shell() {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-canvas grid-dots" data-testid="loading-session">
        <img src="/rubipos-logo-t.png" alt="RubiPOS" className="h-12 w-auto animate-pulse-subtle" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (ERP_ROLES.includes(user.rol)) return <ErpLayout />;
  if (user.rol === INVERSIONISTA) return <InversionistaLayout />;
  return (
    <div className="min-h-screen bg-brand-canvas">
      <HeaderNav />
      <Outlet />
    </div>
  );
}

function Home() {
  const { user } = useAuth();
  if (user?.rol === INVERSIONISTA) return <InversionistaResumen />;
  return ERP_ROLES.includes(user?.rol) ? <ErpDashboard /> : <PosTerminal />;
}

function Guard({ permiso, children }) {
  const { user } = useAuth();
  if (permiso && !(user?.permisos || []).includes(permiso)) return <Navigate to="/" replace />;
  return children;
}

const PAGES = [
  ["/venta", "venta", <PosTerminal />], ["/mis-ventas", "mis_ventas", <MisVentas />], ["/catalogo", "catalogo", <CatalogoPack />], ["/etiquetas", "etiquetas", <Etiquetas />],
  ["/mermas", "mermas", <Mermas />], ["/traspasos", "traspasos", <Traspasos />], ["/traspasos-ventas", "traspasos", <TraspasosVentas />], ["/pedidos-pagos", "traspasos", <PedidosPagos />], ["/trazabilidad", "trazabilidad", <Trazabilidad />], ["/inventario", "inventario", <Inventario />], ["/stock", "inventario", <Stock />],
  ["/admin", "admin", <Admin />], ["/usuarios", "usuarios", <Usuarios />], ["/promociones", "promociones", <Promociones />], ["/cortes", "cortes", <Cortes />],
  ["/reportes", "reportes", <Reportes />], ["/clientes", "clientes", <Clientes />], ["/comisiones", "comisiones", <Comisiones />], ["/gastos", "gastos", <Gastos />],
  ["/conciliacion", "conciliacion", <Conciliacion />], ["/tesoreria", "conciliacion", <Tesoreria />], ["/bancos", "bancos", <Bancos />], ["/ecommerce", "ecommerce", <Ecommerce />], ["/cobro-sistema", "venta", <CobroSistema />], ["/reportes-ia", "reportes_ia", <ReportesIA />], ["/resurtido", "resurtido", <Resurtido />], ["/compras", "compras", <Compras />],
  ["/metas", "metas", <Metas />], ["/saldos-favor", "traspasos", <SaldosFavor />], ["/alertas", "alertas", <Alertas />], ["/migracion", "migracion", <Migracion />],
];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton toastOptions={{ style: { fontFamily: "'Plus Jakarta Sans', sans-serif", borderRadius: 14 } }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/activar" element={<Activar />} />
          <Route element={<Shell />}>
            <Route path="/" element={<Home />} />
            <Route path="/ayuda" element={<Ayuda />} />
            <Route path="/mis-utilidades" element={<InversionistaUtilidades />} />
            {PAGES.map(([path, permiso, el]) => <Route key={path} path={path} element={<Guard permiso={permiso}>{el}</Guard>} />)}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
